import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Pagination
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  Edit,
  Delete,
  FileDownload,
  Close
} from '@mui/icons-material';
import baseurl from '../Baseurl/baseurl';
import * as XLSX from 'xlsx';
import { Button, Card, Input, Eyebrow, Loader, EmptyState } from '../ui';

const CATEGORY_PAGE_SIZE = 10;

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    category_name: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);

  // State for admin permissions
  const [permissions, setPermissions] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false
  });

  // State for permission loading
  const [permissionLoading, setPermissionLoading] = useState(true);

  // Fetch admin permissions
  useEffect(() => {
    const fetchAdminPermissions = async () => {
      try {
        setPermissionLoading(true);
        const role = localStorage.getItem('adminRole');
        const storedToken = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');

        if (role === 'community' && storedToken) {
          // Helper to decode JWT payload safely
          const decodeJwt = (token) => {
            try {
              const payload = token.split('.')[1];
              const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
              return JSON.parse(decodeURIComponent(escape(json)));
            } catch (e) {
              try {
                const json = atob(token.split('.')[1]);
                return JSON.parse(json);
              } catch {
                return null;
              }
            }
          };

          const decoded = decodeJwt(storedToken);

          if (decoded && decoded.id) {
            const res = await fetch(`${baseurl}/api/community_admin/${decoded.id}`);

            if (res.ok) {
              const data = await res.json();

              if (data && data.role) {
                // Find the Category role (case insensitive)
                const categoryRole = data.role.find(r => {
                  const roleLower = r.toLowerCase();
                  return roleLower.includes('category') || roleLower.includes('categories');
                });

                if (categoryRole) {
                  // Check if it has specific permissions
                  if (categoryRole.includes('--')) {
                    const permissionsStr = categoryRole.split('--')[1].trim();
                    const permissionList = permissionsStr.split(',').map(p => p.trim().toLowerCase());

                    setPermissions({
                      canView: true, // If they have any category permission, they can view
                      canAdd: permissionList.includes('add'),
                      canEdit: permissionList.includes('edit'),
                      canDelete: permissionList.includes('delete')
                    });
                  } else {
                    // Only "Category" without specific permissions -> only view
                    setPermissions({
                      canView: true,
                      canAdd: false,
                      canEdit: false,
                      canDelete: false
                    });
                  }
                } else {
                  // No Category role found - default to no permissions
                  setPermissions({
                    canView: false,
                    canAdd: false,
                    canEdit: false,
                    canDelete: false
                  });
                }
              }
            } else {
              // Failed to fetch admin data - default to view permission for safety
              console.error('Failed to fetch admin data');
              setPermissions({
                canView: true,
                canAdd: false,
                canEdit: false,
                canDelete: false
              });
            }
          } else {
            // Failed to decode token - default to view permission for safety
            console.error('Failed to decode token');
            setPermissions({
              canView: true,
              canAdd: false,
              canEdit: false,
              canDelete: false
            });
          }
        } else if (role === 'super') {
          // Super admin has all permissions
          setPermissions({
            canView: true,
            canAdd: true,
            canEdit: true,
            canDelete: true
          });
        } else {
          // No role found - default to no permissions
          setPermissions({
            canView: false,
            canAdd: false,
            canEdit: false,
            canDelete: false
          });
        }
      } catch (err) {
        console.error('Error fetching admin permissions:', err);
        // Default to view permission for safety in case of errors
        setPermissions({
          canView: true,
          canAdd: false,
          canEdit: false,
          canDelete: false
        });
      } finally {
        setPermissionLoading(false);
      }
    };

    fetchAdminPermissions();
  }, []);

  // Fetch categories when permissions are loaded
  useEffect(() => {
    // Only fetch if admin has view permission
    if (!permissionLoading && permissions.canView) {
      fetchCategories();
    } else if (!permissionLoading && !permissions.canView) {
      setLoading(false);
      setError('You do not have permission to view category management');
    }
  }, [permissions.canView, permissionLoading]);

  // Fetch categories data
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${baseurl}/api/category/all`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.msg || 'Failed to fetch categories');
      }

      setCategories(data.data || []);
    } catch (err) {
      setError(err.message || 'An error occurred while fetching categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalFiltered = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / CATEGORY_PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * CATEGORY_PAGE_SIZE;
  const endIndex = Math.min(startIndex + CATEGORY_PAGE_SIZE, totalFiltered);
  const paginatedCategories = filteredCategories.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Keep page in valid range
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const handlePageChange = (event, newPage) => {
    if (typeof newPage === 'number' && newPage >= 1) {
      setPage(Math.min(newPage, totalPages));
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setFormData({ category_name: category.category_name });
      setSelectedCategory(category);
    } else {
      setFormData({ category_name: '' });
      setSelectedCategory(null);
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ category_name: '' });
    setSelectedCategory(null);
    setFormErrors({});
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.category_name.trim()) {
      errors.category_name = 'Category name is required';
    } else if (formData.category_name.trim().length < 2) {
      errors.category_name = 'Category name must be at least 2 characters long';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
      const url = selectedCategory
        ? `${baseurl}/api/category/update/${selectedCategory.cid}`
        : `${baseurl}/api/category/register`;

      const response = await fetch(url, {
        method: selectedCategory ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.msg || 'Failed to save category');
      }

      setSnackbar({
        open: true,
        message: selectedCategory ? 'Category updated successfully' : 'Category created successfully',
        severity: 'success'
      });

      handleCloseDialog();
      fetchCategories(); // Refresh the list
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save category',
        severity: 'error'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
      const response = await fetch(`${baseurl}/api/category/delete/${selectedCategory.cid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete category');
      }

      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success'
      });

      handleCloseDeleteDialog();
      fetchCategories(); // Refresh the list
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete category',
        severity: 'error'
      });
    }
  };

  const handleExport = () => {
    const exportData = categories.map(category => ({
      'ID': category.cid,
      'Category Name': category.category_name,
      'Created At': new Date(category.createdAt).toLocaleDateString(),
      'Updated At': new Date(category.updatedAt).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories');
    XLSX.writeFile(wb, `categories_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Themed sx for MUI Dialog surfaces
  const dialogPaperSx = {
    backgroundColor: 'background.paper',
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    boxShadow: 'var(--shadow-card)'
  };

  // Show loading state while checking permissions
  if (permissionLoading) {
    return (
      <Card className="mx-auto max-w-md">
        <Loader label="Checking permissions…" />
      </Card>
    );
  }

  // If admin doesn't have view permission, show access denied message
  if (!permissions.canView) {
    return (
      <div className="space-y-6">
        <div className="mb-5 border-b border-line/10 pb-4">
          <Eyebrow>Categories</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Category <em className="kbc-serif">Management</em>
          </h1>
        </div>
        <Card>
          <EmptyState
            title="Access Denied"
            description="You do not have permission to view the Category Management module."
            action={
              <Button variant="primary" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Eyebrow>Categories</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Category <em className="kbc-serif">Management</em>
          </h1>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
            Manage the business categories used across the directory.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {permissions.canAdd && (
            <Button
              variant="primary"
              leftIcon={<Add fontSize="small" />}
              onClick={() => handleOpenDialog()}
            >
              Add Category
            </Button>
          )}
          <Button
            variant="ghost"
            leftIcon={<FileDownload fontSize="small" />}
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Main Content Card */}
      <Card padding="none">
        {/* Controls */}
        <div className="p-5">
          <div className="w-full md:max-w-sm">
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search fontSize="small" />}
            />
          </div>
        </div>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { borderColor: 'divider' } }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', backgroundColor: 'transparent' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', backgroundColor: 'transparent' }}>Category Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', backgroundColor: 'transparent' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, borderColor: 'divider' }}>
                    <Loader label="Loading categories…" />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, borderColor: 'divider' }}>
                    <Typography sx={{ color: 'error.main' }}>{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 0, borderColor: 'divider' }}>
                    <EmptyState
                      title="No categories found"
                      description="Try adjusting your search, or add a new category to get started."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCategories.map((category, index) => (
                  <TableRow
                    key={category.cid}
                    sx={{
                      '& td': { borderColor: 'divider', color: 'text.secondary' },
                      '&:hover': { backgroundColor: 'rgba(41,245,166,0.05)' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {startIndex + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {category.category_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {permissions.canView && (
                          <IconButton
                            size="small"
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            onClick={() => handleViewCategory(category)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        )}
                        {permissions.canEdit && (
                          <IconButton
                            size="small"
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            onClick={() => handleOpenDialog(category)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {permissions.canDelete && (
                          <IconButton
                            size="small"
                            sx={{ color: 'error.main' }}
                            onClick={() => handleDeleteClick(category)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {totalFiltered === 0
              ? 'Showing 0 categories'
              : `Showing ${startIndex + 1}-${endIndex} of ${totalFiltered} categories (${CATEGORY_PAGE_SIZE} per page)`}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': { color: 'text.secondary' },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.dark' }
              }
            }}
          />
        </Box>
      </Card>

      {/* Add/Edit Category Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, color: 'text.primary' }}>
          {selectedCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={formData.category_name}
            onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
            error={!!formErrors.category_name}
            helperText={formErrors.category_name}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="ghost" onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit}>
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Category Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, color: 'text.primary' }}>
          Category Details
          <IconButton
            aria-label="close"
            onClick={handleCloseViewDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCategory && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ color: 'text.secondary' }}>
                <strong style={{ color: 'inherit' }}>Category Name:</strong> {selectedCategory.category_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="ghost" onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, color: 'text.primary' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete the category "{selectedCategory?.category_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="ghost" onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            minWidth: 300,
            zIndex: 9999
          }}
        >
          {snackbar.message}
        </Alert>
      )}
    </div>
  );
};

export default Category;
